import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtErr, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { ConfirmDialog, ActivationModePicker, copyText } from "@/components/common";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Search, Trash2 } from "lucide-react";

const PORTAL_LABELS = { non_cree: "Non créé", en_attente: "En attente", actif: "Actif", desactive: "Désactivé" };

export default function ClientsPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const isSuper = user?.role === "super_admin";
  const canManageAccess = ["super_admin", "company_admin"].includes(user?.role);
  const canDelete = user?.role === "super_admin" && user?.user_type === "staff_suivio";
  const [selected, setSelected] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact_name: "", contact_email: "", contact_phone: "", address: "", notes: "", company_id: "" });
  const [search, setSearch] = useState("");
  const [fCompany, setFCompany] = useState("");
  const [fPortal, setFPortal] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [credentials, setCredentials] = useState(null);
  const [accessTarget, setAccessTarget] = useState(null);
  const [accessMode, setAccessMode] = useState("invite");
  const [accessPwd, setAccessPwd] = useState("");
  const [accessConfirm, setAccessConfirm] = useState("");
  const [accessForm, setAccessForm] = useState({ first_name: "", last_name: "", email: "", phone: "" });

  const openAccess = (c) => {
    setAccessForm({ first_name: "", last_name: "", email: "", phone: "" });
    setAccessMode("invite"); setAccessPwd(""); setAccessConfirm("");
    setAccessTarget(c);
  };

  const load = () => {
    api.get("/clients", { params: { include_archived: true } }).then(({ data }) => setClients(data)).catch((e) => toast.error(fmtErr(e)));
    if (isSuper) api.get("/companies").then(({ data }) => setCompanies(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const visible = useMemo(() => {
    let list = clients || [];
    if (!showArchived) list = list.filter((c) => !c.archived);
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((c) => c.name.toLowerCase().includes(s) || (c.contact_name || "").toLowerCase().includes(s) || (c.contact_email || "").toLowerCase().includes(s));
    }
    if (fCompany) list = list.filter((c) => c.company_id === fCompany);
    if (fPortal) list = list.filter((c) => (c.portal_access?.status || "non_cree") === fPortal);
    return list;
  }, [clients, search, fCompany, fPortal, showArchived]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/clients", {
        name: form.name, contact_name: form.contact_name || null,
        contact_email: form.contact_email || null, contact_phone: form.contact_phone || null,
        address: form.address || null, notes: form.notes || null,
        company_id: isSuper ? form.company_id : null,
      });
      setOpen(false);
      setForm({ name: "", contact_name: "", contact_email: "", contact_phone: "", address: "", notes: "", company_id: "" });
      load();
      toast.success("Client entreprise créé");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/clients/${editTarget.id}`, {
        name: editTarget.name, contact_name: editTarget.contact_name || null,
        contact_email: editTarget.contact_email || null, contact_phone: editTarget.contact_phone || null,
        address: editTarget.address || null, notes: editTarget.notes || null,
      });
      toast.success("Client mis à jour");
      setEditTarget(null);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setSaving(false);
    }
  };

  const createPortalUser = async () => {
    if (!accessTarget) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/clients/${accessTarget.id}/portal-users`, {
        first_name: accessForm.first_name || null, last_name: accessForm.last_name || null,
        email: accessForm.email, phone: accessForm.phone || null,
        activation_mode: accessMode,
        password: accessMode === "manual" ? accessPwd : null,
        password_confirm: accessMode === "manual" ? accessConfirm : null,
      });
      if (data?.activation_link) setCredentials({ email: data.email, link: data.activation_link, emailStatus: data.email_status });
      const suffix = data?.email_status === "envoye" ? " — email envoyé via Brevo"
        : data?.email_status === "erreur" ? " — échec d'envoi email (lien de secours affiché)"
        : data?.email_status === "simule_envoye" ? " — email simulé" : "";
      toast.success((accessMode === "invite" ? "Utilisateur portail créé — invitation envoyée" : "Utilisateur portail créé et actif") + suffix);
      setAccessTarget(null);
      setAccessMode("invite"); setAccessPwd(""); setAccessConfirm("");
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setSaving(false); }
  };

  const doArchive = async () => {
    try {
      if (archiveTarget.archived) {
        await api.post(`/clients/${archiveTarget.id}/restore`);
        toast.success("Client restauré");
      } else {
        await api.post(`/clients/${archiveTarget.id}/archive`);
        toast.success("Client archivé — accès portail désactivé");
      }
      load();
    } catch (e) { toast.error(fmtErr(e)); throw e; }
  };

  const toggleOne = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const visibleIds = visible.map((c) => c.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
  const toggleAll = () => setSelected(allSelected ? [] : visibleIds);

  const doDelete = async () => {
    try {
      await api.delete(`/clients/${deleteTarget.id}`);
      toast.success(`Client ${deleteTarget.name} supprimé`);
      setSelected((s) => s.filter((x) => x !== deleteTarget.id));
      setDeleteTarget(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doBulkDelete = async () => {
    try {
      const { data } = await api.post("/clients/bulk-delete", { ids: selected });
      toast.success(`${data.deleted} client(s) supprimé(s)`);
      setSelected([]);
      setBulkOpen(false);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="clients-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Clients entreprise</h1>
          <p className="mt-1 text-sm text-muted-foreground">Les clients entreprise des sociétés clientes — distincts des utilisateurs internes.</p>
        </div>
        <Button data-testid="create-client-button" onClick={() => setOpen(true)} className="rounded-sm bg-primary text-white hover:bg-red-700">
          <Plus className="mr-2 h-4 w-4" /> Nouveau client
        </Button>
      </div>

      <div className="mb-5 flex flex-wrap gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="search-clients" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, contact ou email…" className="w-64 rounded-sm pl-9" />
        </div>
        {isSuper && (
          <Select value={fCompany} onValueChange={(v) => setFCompany(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="filter-client-company" className="w-48 rounded-sm"><SelectValue placeholder="Société : toutes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Société : toutes</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={fPortal} onValueChange={(v) => setFPortal(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-client-portal" className="w-52 rounded-sm"><SelectValue placeholder="Accès portail : tous" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Accès portail : tous</SelectItem>
            {Object.entries(PORTAL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" data-testid="filter-client-archived" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="accent-red-600" />
          Afficher les archivés
        </label>
      </div>

      {canDelete && selected.length > 0 && (
        <div data-testid="clients-bulk-bar" className="mb-3 flex items-center justify-between rounded-sm border border-primary/40 bg-primary/5 px-4 py-2.5">
          <span className="text-sm font-medium" data-testid="clients-bulk-count">{selected.length} client(s) sélectionné(s)</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setSelected([])} data-testid="clients-bulk-clear">Annuler</Button>
            <Button size="sm" className="rounded-sm bg-primary text-white hover:bg-red-700" onClick={() => setBulkOpen(true)} data-testid="clients-bulk-delete-button">
              <Trash2 className="mr-2 h-4 w-4" /> Supprimer la sélection
            </Button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-sm border border-border bg-card">
        <table className="w-full text-sm" data-testid="clients-table">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              {canDelete && (
                <th className="px-5 py-3 w-10">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="clients-select-all" aria-label="Tout sélectionner" />
                </th>
              )}
              <th className="px-5 py-3 font-semibold">Client</th>
              <th className="px-5 py-3 font-semibold">Contact</th>
              {isSuper && <th className="px-5 py-3 font-semibold">Société</th>}
              <th className="px-5 py-3 font-semibold">Accès portail</th>
              <th className="px-5 py-3 font-semibold">Tickets</th>
              <th className="px-5 py-3 font-semibold">Créé le</th>
              <th className="px-5 py-3 font-semibold"></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => (
              <tr key={c.id} className={`border-b border-border last:border-b-0 hover:bg-accent transition-colors duration-200 ${c.archived ? "opacity-60" : ""}`} data-testid={`client-row-${c.id}`}>
                {canDelete && (
                  <td className="px-5 py-3">
                    <Checkbox checked={selected.includes(c.id)} onCheckedChange={() => toggleOne(c.id)}
                      data-testid={`client-select-${c.id}`} aria-label={`Sélectionner ${c.name}`} />
                  </td>
                )}
                <td className="px-5 py-3">
                  <Link to={`/app/clients/${c.id}`} data-testid={`client-link-${c.id}`} className="font-semibold hover:text-primary">{c.name}</Link>
                  {c.archived && <span className="ml-2 rounded-sm bg-neutral-200 px-1.5 py-0.5 text-xs text-neutral-500 dark:bg-neutral-800">Archivé</span>}
                </td>
                <td className="px-5 py-3">
                  <div>{c.contact_name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{c.contact_email}</div>
                </td>
                {isSuper && <td className="px-5 py-3">{c.company_name}</td>}
                <td className="px-5 py-3">
                  <span className={`rounded-sm px-2 py-0.5 text-xs font-semibold ${c.portal_access?.status === "actif" ? "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300" : c.portal_access?.status === "en_attente" ? "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300" : c.portal_access?.status === "desactive" ? "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300" : "bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300"}`}>
                    {PORTAL_LABELS[c.portal_access?.status] || "Non créé"}{c.portal_access?.count > 1 ? ` (${c.portal_access.count})` : ""}
                  </span>
                </td>
                <td className="px-5 py-3">{c.tickets_count}</td>
                <td className="px-5 py-3 text-muted-foreground">{fmtDate(c.created_at)}</td>
                <td className="px-5 py-3 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`client-actions-${c.id}`} className="rounded-sm border border-border p-1.5 text-muted-foreground hover:text-foreground transition-colors">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-sm">
                      <DropdownMenuItem asChild data-testid={`client-action-view-${c.id}`}>
                        <Link to={`/app/clients/${c.id}`}>Voir le détail / tickets</Link>
                      </DropdownMenuItem>
                      {!c.archived && (
                        <DropdownMenuItem data-testid={`client-action-edit-${c.id}`} onClick={() => setEditTarget({ ...c })}>Modifier</DropdownMenuItem>
                      )}
                      {canManageAccess && !c.archived && (
                        <DropdownMenuItem data-testid={`client-action-create-access-${c.id}`} onClick={() => openAccess(c)}>Nouvel utilisateur portail</DropdownMenuItem>
                      )}
                      {canManageAccess && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`client-action-archive-${c.id}`} onClick={() => setArchiveTarget(c)} className="text-primary">
                            {c.archived ? "Restaurer" : "Archiver"}
                          </DropdownMenuItem>
                        </>
                      )}
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem data-testid={`client-action-delete-${c.id}`} onClick={() => setDeleteTarget(c)} className="text-primary font-medium">
                            <Trash2 className="mr-2 h-4 w-4" /> Supprimer définitivement
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {clients && visible.length === 0 && <div className="p-8 text-center text-sm text-muted-foreground">Aucun client entreprise avec ces filtres.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm" data-testid="create-client-dialog">
          <DialogHeader>
            <DialogTitle>Nouveau client entreprise</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom du client *</Label>
              <Input data-testid="client-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" placeholder="Ex : Résidence Les Pins" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contact principal</Label>
                <Input data-testid="client-contact-name-input" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="rounded-sm" />
              </div>
              <div className="space-y-2">
                <Label>Email du contact</Label>
                <Input data-testid="client-contact-email-input" type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} className="rounded-sm" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input data-testid="client-contact-phone-input" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="rounded-sm" />
              </div>
              {isSuper && (
                <div className="space-y-2">
                  <Label>Société propriétaire *</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                    <SelectTrigger data-testid="client-company-select" className="rounded-sm"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Adresse</Label>
              <Input data-testid="client-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label>Notes internes</Label>
              <Input data-testid="client-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" />
            </div>
            <DialogFooter>
              <Button data-testid="client-submit-button" type="submit" disabled={saving || (isSuper && !form.company_id)} className="rounded-sm bg-primary text-white hover:bg-red-700">
                {saving ? "Création…" : "Créer le client"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editTarget} onOpenChange={() => setEditTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="edit-client-dialog">
          <DialogHeader>
            <DialogTitle>Modifier le client entreprise</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={saveEdit} className="space-y-4">
              <div className="space-y-2">
                <Label>Nom du client</Label>
                <Input data-testid="edit-client-name" required value={editTarget.name} onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })} className="rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact principal</Label>
                  <Input data-testid="edit-client-contact-name" value={editTarget.contact_name || ""} onChange={(e) => setEditTarget({ ...editTarget, contact_name: e.target.value })} className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Email du contact</Label>
                  <Input data-testid="edit-client-contact-email" type="email" value={editTarget.contact_email || ""} onChange={(e) => setEditTarget({ ...editTarget, contact_email: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input data-testid="edit-client-contact-phone" value={editTarget.contact_phone || ""} onChange={(e) => setEditTarget({ ...editTarget, contact_phone: e.target.value })} className="rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Input data-testid="edit-client-address" value={editTarget.address || ""} onChange={(e) => setEditTarget({ ...editTarget, address: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes internes</Label>
                <Input data-testid="edit-client-notes" value={editTarget.notes || ""} onChange={(e) => setEditTarget({ ...editTarget, notes: e.target.value })} className="rounded-sm" />
              </div>
              <DialogFooter>
                <Button data-testid="edit-client-submit" type="submit" disabled={saving} className="rounded-sm bg-primary text-white hover:bg-red-700">
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!credentials} onOpenChange={() => setCredentials(null)}>
        <DialogContent className="rounded-sm" data-testid="portal-credentials-dialog">
          <DialogHeader>
            <DialogTitle>Invitation portail</DialogTitle>
          </DialogHeader>
          {credentials && (
            <div className="overflow-hidden rounded-sm border border-border p-4 text-sm">
              <div>{credentials.email}</div>
              <div className="mt-2 text-xs text-muted-foreground">Lien d'activation (valide 72 h)</div>
              <div className="mt-1 flex items-center gap-2">
                <code data-testid="portal-activation-link" className="min-w-0 flex-1 truncate rounded-sm bg-muted px-2 py-1 text-xs">{credentials.link}</code>
                <Button size="sm" variant="outline" data-testid="copy-portal-activation-link" className="shrink-0 rounded-sm"
                  onClick={() => copyText(credentials.link)}>Copier</Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground" data-testid="portal-invitation-email-note">
                {credentials.emailStatus === "envoye" ? "Email d'invitation envoyé via Brevo — le lien ci-dessus n'est qu'un secours."
                  : credentials.emailStatus === "erreur" ? "Échec d'envoi de l'email — transmettez le lien ci-dessus ou renvoyez l'invitation."
                  : "Email d'invitation simulé (aucun envoi réel) — transmettez le lien ci-dessus au client."}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!accessTarget} onOpenChange={() => setAccessTarget(null)}>
        <DialogContent className="rounded-sm" data-testid="create-access-dialog">
          <DialogHeader>
            <DialogTitle>Nouvel utilisateur portail — {accessTarget?.name}</DialogTitle>
          </DialogHeader>
          {accessTarget && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Cet utilisateur pourra se connecter au portail de <b>{accessTarget.company_name}</b> pour créer et suivre les demandes de <b>{accessTarget.name}</b>.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom</Label>
                  <Input data-testid="portal-user-first-name" value={accessForm.first_name} onChange={(e) => setAccessForm({ ...accessForm, first_name: e.target.value })} className="rounded-sm" placeholder="Ex : Claire" />
                </div>
                <div className="space-y-2">
                  <Label>Nom</Label>
                  <Input data-testid="portal-user-last-name" value={accessForm.last_name} onChange={(e) => setAccessForm({ ...accessForm, last_name: e.target.value })} className="rounded-sm" placeholder="Ex : Martin" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input data-testid="portal-user-email" type="email" required value={accessForm.email} onChange={(e) => setAccessForm({ ...accessForm, email: e.target.value })} className="rounded-sm" placeholder="prenom@client.fr" />
                </div>
                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input data-testid="portal-user-phone" value={accessForm.phone} onChange={(e) => setAccessForm({ ...accessForm, phone: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <ActivationModePicker testid="portal-activation"
                inviteText="Un email d'invitation sera envoyé avec un lien d'activation valide 72 h (le lien reste affiché en secours)."
                mode={accessMode} onModeChange={setAccessMode}
                password={accessPwd} onPasswordChange={setAccessPwd}
                confirm={accessConfirm} onConfirmChange={setAccessConfirm} />
              <DialogFooter>
                <Button data-testid="create-access-submit"
                  disabled={saving || !accessForm.email || (accessMode === "manual" && (accessPwd.length < 8 || accessPwd !== accessConfirm))}
                  onClick={createPortalUser}
                  className="rounded-sm bg-primary text-white hover:bg-red-700">
                  {saving ? "Création…" : "Créer l'utilisateur portail"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!archiveTarget} onOpenChange={() => setArchiveTarget(null)}
        title={archiveTarget?.archived ? `Restaurer ${archiveTarget?.name} ?` : `Archiver ${archiveTarget?.name} ?`}
        description={archiveTarget?.archived
          ? "Le client réapparaîtra dans la liste. L'accès portail restera désactivé (réactivez-le si besoin)."
          : "Êtes-vous sûr de vouloir archiver ce client entreprise ? Son accès portail sera désactivé et il sera masqué des listes. Les données et tickets restent conservés. Action journalisée."}
        confirmLabel={archiveTarget?.archived ? "Restaurer" : "Archiver"}
        onConfirm={doArchive} testid="archive-client-dialog" />

      <ConfirmDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}
        title={`Supprimer définitivement ${deleteTarget?.name} ?`}
        description="Cette action est IRRÉVERSIBLE. Le client entreprise et TOUTES ses données liées (utilisateurs portail, tickets, messages, fichiers) seront définitivement supprimés. Action journalisée."
        confirmLabel="Supprimer définitivement" onConfirm={doDelete} testid="delete-client-dialog" />

      <ConfirmDialog open={bulkOpen} onOpenChange={setBulkOpen}
        title={`Supprimer ${selected.length} client(s) ?`}
        description="Cette action est IRRÉVERSIBLE. Chaque client sélectionné et TOUTES ses données liées (utilisateurs portail, tickets, messages, fichiers) seront définitivement supprimés. Action journalisée."
        confirmLabel="Supprimer la sélection" onConfirm={doBulkDelete} testid="bulk-delete-clients-dialog" />
    </div>
  );
}
